import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { Plus, X, FolderOpen, Settings, Calendar, Layers, ChevronRight } from 'lucide-react';
import { message, Tooltip } from "antd";
import axios from "../../utils/axios";
import Header from '../header/header2';

const ProjectCard = ({ project, onClick }) => {
  const getStatusColor = (status) => {
    const colors = {
      'Active': 'bg-green-100 text-green-800 border-green-200',
      'In Progress': 'bg-blue-100 text-blue-800 border-blue-200',
      'Completed': 'bg-gray-100 text-gray-800 border-gray-200',
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  return (
    <div
      onClick={() => onClick(project)}
      className="group bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl hover:border-blue-200 transition-all duration-300 overflow-hidden cursor-pointer relative"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/0 to-purple-50/0 group-hover:from-blue-50/50 group-hover:to-purple-50/50 transition-all duration-300"></div>

      <div className="relative p-6">
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-start space-x-4 flex-1 min-w-0">
            <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-md group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
              <Layers className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-gray-900 text-lg mb-2 truncate group-hover:text-blue-600 transition-colors">
                {project.projectname}
              </h3>
              <span className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold border ${getStatusColor(project.status)}`}>
                <div className="w-1.5 h-1.5 bg-current rounded-full mr-2"></div>
                {project.status}
              </span>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
        </div>

        <div className="space-y-3">
          <div className="flex items-center text-sm">
            <div className="w-8 h-8 bg-gray-50 rounded-lg flex items-center justify-center mr-3">
              <Calendar className="w-4 h-4 text-gray-500" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-500 font-medium mb-0.5">Created</p>
              <p className="text-sm text-gray-900 font-semibold">{project.createdAt}</p>
            </div>
          </div>
        </div>

        <div className="mt-5 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500 font-medium">Click to open</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const Createproject = () => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ projectname: '' });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const citriot = JSON.parse(localStorage.getItem("citriot")) || {};
  const organizationId = localStorage.getItem("organizationId");

  const remainingSlots = user?.projects ?? 0;
  const canCreate = remainingSlots > 0;

  // Load user from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error("Failed to parse user from localStorage");
      }
    }
  }, []);

  const fetchProjects = async () => {
    if (!user?.username) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post("fetch-createprojects", {
        username: user.username,
        organizationId: organizationId
      });

      if (response.status === 200 && response.data.projects) {
        const mappedProjects = response.data.projects.map((proj) => ({
          id: proj._id,
          projectname: proj.projectname,
          createdAt: new Date(proj.createdAt).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
          }),
          status: "Active",
          code: proj.code,
          selectCamera: proj.selectCamera,
          addcamera: proj.addcamera,
        }));
        setProjects(mappedProjects);
      } else {
        message.error(response.data?.message || "Failed to fetch projects");
      }
    } catch (error) {
      message.error("Something went wrong while fetching projects.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [user]);

  const handleSubmit = async (e) => {
  e.preventDefault();
  
  const projectName = formData.projectname.trim();
  
  if (!projectName) {
    message.warning("Project name is required.");
    return;
  }

  if (projectName.length < 4) {
    message.warning("Project name must be at least 4 characters long.");
    return;
  }

  // Optional: more strict validation
  if (!/^[a-zA-Z0-9-_ ]{4,}$/.test(projectName)) {
    message.warning(
      "Project name can only contain letters, numbers, spaces, hyphens and underscores."
    );
    return;
  }

  // Optional: prevent only numbers or suspicious names
  if (/^\d+$/.test(projectName)) {
    message.warning("Project name cannot be only numbers.");
    return;
  }

  setCreating(true);

  try {
    // ... rest of your axios call remains the same
    const response = await axios.post("create-project", {
      projectname: projectName,
      username: user.username,
      organizationId: organizationId
    });

    if (response.status === 200) {
      const updatedUser = {
        ...user,
        projects: remainingSlots - 1,
      };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));

      await fetchProjects();

      setFormData({ projectname: "" });
      setIsModalOpen(false);
      message.success("Project created successfully!");
    }
  } catch (error) {
    if (error.response?.status === 400) {
      message.warning("Project limit exhausted. Please contact admin.");
    } else {
      message.error("Failed to create project. Please try again.");
    }
  } finally {
    setCreating(false);
  }
};

  const handleProjectClick = async (project) => {
    try {
      localStorage.setItem("projectname", project.projectname);

      const response = await axios.post("/check-project-process", {
        username: user.username,
        projectName: project.projectname,
        organizationId: organizationId
      });

      const {
        categorySelected,
        selectCamera,
        addcamera
      } = response.data;

      // 1️⃣ Category not selected → Home
      if (!categorySelected) {
        navigate("/home", {
          state: { projectname: project.projectname }
        });
        return;
      }

      // 2️⃣ Select camera step
      if (!selectCamera) {
        navigate("/selectcamera", {
          state: { projectname: project.projectname }
        });
        return;
      }

      // 3️⃣ Add camera step
      if (!addcamera) {
        navigate("/add-camera", {
          state: { projectname: project.projectname }
        });
        return;
      }

      // 4️⃣ All steps done → Sidebar
      // navigate("/sidebar", {
      //   state: { projectname: project.projectname }
      // });

      navigate("/selectmodel", {
        state: { projectname: project.projectname }
      });

    } catch (error) {
      console.error(error);
      message.error("Failed to check project status");
    }
  };

  if (!user) {
    navigate('/loginconfiguration')
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Header auth={citriot?.auth} />

      <header className="top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Settings className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  ProjectHub
                </h1>
                <p className="text-sm text-gray-500">Manage and track your machine learning projects</p>
              </div>
            </div>

            {projects.length > 0 ? <Tooltip
              title={canCreate ? "Create a new project" : "Project limit exhausted. Please contact admin."}
            >
              <button
                onClick={canCreate ? () => setIsModalOpen(true) : undefined}
                disabled={!canCreate}
                className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                  canCreate
                    ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg hover:scale-[1.02]"
                    : "bg-gray-400 text-white cursor-not-allowed"
                }`}
              >
                <Plus className="w-5 h-5" />
                <span>Create Project</span>
              </button>
            </Tooltip> : <></>}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-72 bg-gray-100 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="relative inline-block mb-6">
              <div className="absolute inset-0 bg-blue-100 rounded-full blur-2xl opacity-50"></div>
              <div className="relative w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl flex items-center justify-center">
                <FolderOpen className="w-10 h-10 text-blue-600" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No projects yet</h3>
            <p className="text-gray-500 mb-8 max-w-md mx-auto">
              Start your journey by creating your first machine learning project. It only takes a moment!
            </p>

            {canCreate ? (
              <button
                onClick={() => setIsModalOpen(true)}
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg transition-all"
              >
                <Plus className="w-5 h-5" />
                <span>Create Your First Project</span>
              </button>
            ) : (
              <p className="text-amber-600 font-medium">Project creation limit reached. Please contact admin.</p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <ProjectCard key={project.id} project={project} onClick={handleProjectClick} />
            ))}
          </div>
        )}
      </main>
      

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900">Create New Project</h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label htmlFor="projectName" className="block text-sm font-semibold text-gray-900 mb-2">
                  Project Name
                </label>
                <input
                  type="text"
                  id="projectName"
                  value={formData.projectname}
                  onChange={(e) => setFormData({ projectname: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  placeholder="Enter project name"
                  required
                  disabled={creating}
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  disabled={creating}
                  className="flex-1 px-6 py-3 border border-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className={`flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold transition-all ${
                    creating ? "opacity-75 cursor-not-allowed" : "hover:from-blue-700 hover:to-purple-700 hover:shadow-xl"
                  }`}
                >
                  {creating ? "Creating..." : "Create Project"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Createproject;